import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2 } from 'lucide-react';
import {
  listSelections,
  createSelection,
  deleteSelection,
  type SelectionCreateInput,
} from '@/lib/selections-api';
import { TypeMultiSelect } from '@/components/selections/TypeMultiSelect';
import { MultiSelectDropdown } from '@/components/selections/MultiSelectDropdown';
import { TargetInput } from '@/components/selections/TargetInput';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { deriveShortlist, getSelectionTarget, targetLabel, type TargetField } from '@/lib/target';
import type { Selection } from '@littlebooker/dal';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2';

// Happening lifecycle statuses (HappeningStatus).
const STATUS_OPTIONS = ['draft', 'scheduled', 'confirmed', 'completed', 'cancelled'];

const STATE_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  complete: 'Complete',
};

function CreateSelectionDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [finalTarget, setFinalTarget] = useState<TargetField>({ mode: 'pct', value: 15 });
  // Basic scope controls; resolved server-side via listHappenings.
  const [scopeTypes, setScopeTypes] = useState<string[]>(['recording']);
  const [scopeStatuses, setScopeStatuses] = useState<string[]>([]);
  const [scopeFrom, setScopeFrom] = useState('');
  const [scopeTo, setScopeTo] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: () => {
      const scope: Record<string, unknown> = {};
      if (scopeTypes.length > 0) scope.types = scopeTypes;
      if (scopeStatuses.length > 0) scope.statuses = scopeStatuses;
      // Treat the date inputs as inclusive local-day boundaries — otherwise a
      // bare date parses as UTC midnight and the "To" day is excluded entirely.
      if (scopeFrom) scope.start_time_gte = new Date(`${scopeFrom}T00:00:00`).toISOString();
      if (scopeTo) scope.start_time_lte = new Date(`${scopeTo}T23:59:59.999`).toISOString();

      const target = { final: finalTarget, shortlist: deriveShortlist(finalTarget) };
      const input: SelectionCreateInput = {
        title: title.trim(),
        description: description.trim() || null,
        scope,
        settings: { target },
        // owner_id defaults to the acting user server-side.
      };
      return createSelection(input);
    },
    onSuccess: (selection) => {
      queryClient.invalidateQueries({ queryKey: ['selections'] });
      onClose();
      navigate(`/selections/${selection.id}`);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to create selection'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New selection</DialogTitle>
            <DialogDescription>
              A structured, multi-round staff vote over a set of happenings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <input
                className={inputClass}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="End-of-season staff picks"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className={inputClass}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <TargetInput
              label="Final list size"
              explanation="How many shows end up in the published result. Enter a % of scope or an absolute number — the shortlist is sized from this (≈2×), and you can fine-tune both later on the Scope tab."
              field={finalTarget}
              onChange={setFinalTarget}
              scopeSize={0}
            />

            <div className="rounded-md border p-3">
              <p className="text-sm font-medium">Scope</p>
              <p className="mb-2 text-xs text-muted-foreground">
                Which happenings are candidates. You can preview and refine this after creating.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium">Types</label>
                  <TypeMultiSelect value={scopeTypes} onChange={setScopeTypes} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium">Status</label>
                  <MultiSelectDropdown
                    value={scopeStatuses}
                    onChange={setScopeStatuses}
                    options={STATUS_OPTIONS}
                    placeholder="(any status)"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">From</label>
                  <input
                    className={inputClass}
                    type="date"
                    value={scopeFrom}
                    onChange={(e) => setScopeFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">To</label>
                  <input
                    className={inputClass}
                    type="date"
                    value={scopeTo}
                    onChange={(e) => setScopeTo(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create selection'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SelectionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState<Selection | null>(null);

  const { data: selections = [], isLoading } = useQuery({
    queryKey: ['selections'],
    queryFn: () => listSelections(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSelection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selections'] });
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Selections</h1>
          <p className="text-muted-foreground">
            Converge a small group on a subset of happenings through structured rounds.
          </p>
        </div>
        {isOwner && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New selection
          </Button>
        )}
      </div>

      {isLoading ? (
        <p>Loading…</p>
      ) : selections.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No selections yet</CardTitle>
            <CardDescription>
              {isOwner
                ? 'Create a selection to run a structured, blind, multi-round staff vote.'
                : 'No selections have been created for this account yet.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Created</TableHead>
                {isOwner && <TableHead className="w-12">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {selections.map((s) => (
                <TableRow
                  key={s.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/selections/${s.id}`)}
                >
                  <TableCell className="font-medium">{s.title}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {STATE_LABELS[s.state] ?? s.state}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {targetLabel(getSelectionTarget(s).final)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(s.created_at).toLocaleDateString()}
                  </TableCell>
                  {isOwner && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleting(s)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {showCreate && <CreateSelectionDialog onClose={() => setShowCreate(false)} />}

      {deleting && (
        <DeleteConfirmationDialog
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          onConfirm={() => deleteMutation.mutate(deleting.id)}
          entityName={deleting.title}
          entityType="selection"
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
